import { normalizeDisplayStatus } from "../../utils/pricingOptions";

export const isDecorationRoutingToken = (value) => {
  const normalized = String(value ?? "").trim();
  return /^supplier\|\$/i.test(normalized) || /^thirdparty\|/i.test(normalized);
};

export const buildSavedDecorationSourceOptions = (decorations = [], sourceOptions = []) => {
  const existing = Array.isArray(sourceOptions) ? [...sourceOptions] : [];
  const seen = new Set(existing.map((option) => String(option?.value || "").toLowerCase()));

  (Array.isArray(decorations) ? decorations : []).forEach((decoration) => {
    const details =
      decoration?.DecoratorDetails ||
      decoration?.decoratorDetails ||
      decoration?.routing ||
      {};
    const source = String(details?.source || decoration?.selectedSource || "").trim();
    if (!source || seen.has(source.toLowerCase())) return;

    const methodName = String(
      details?.methodName ||
      decoration?.documentDecoration?.method?.name ||
      ""
    ).trim();

    existing.push({
      value: source,
      label:
        methodName ||
        (source === "supplier-custom"
          ? "Custom Request (Supplier)"
          : source === "elite"
            ? "Decoration Network"
            : source),
      saved: true,
    });
    seen.add(source.toLowerCase());
  });

  return existing;
};

export const resolveArtworkImprintTypeForEdit = (artwork = {}, decoratorDetails = {}) => {
  const imprintType = String(
    artwork?.ImprintType ?? artwork?.imprintType ?? ""
  ).trim();

  // Historical supplier/third-party snapshots sometimes stored the routing key
  // in ArtWork.ImprintType. Keep custom text intact, but never expose a routing
  // token as editable artwork wording.
  if (!isDecorationRoutingToken(imprintType)) return imprintType;

  const methodName = String(decoratorDetails?.methodName || "").trim();
  if (!methodName) return "";

  return methodName.replace(/\s*\(Supplier\)\s*$/i, "").trim();
};

export const buildSupplierDecorationSourceOptions = (productData) => {
  const methods = productData?.Imprinting?.Methods?.Values || [];
  const uniqueMethods = methods.filter((method, index, allMethods) =>
    String(method?.Code || '').trim().toUpperCase() !== '$UNI' &&
    !/^unimprinted$/i.test(String(method?.Name || '').trim()) &&
    allMethods.findIndex((candidate) => candidate.Code === method.Code) === index
  );

  return [
    ...uniqueMethods.map((method) => ({
      label: `${method.Name} (Supplier)`,
      value: `supplier|${method.Code}`,
    })),
    { label: "Decoration Network", value: "elite" },
  ];
};

export const mapDecorationsFromApi = (
  decorationsFromApi = [],
  defaultLocation,
  sourceOptions = []
) => {
  return (decorationsFromApi || []).map((decoration) => {
    const dd =
      decoration.DecoratorDetails ||
      decoration.decoratorDetails ||
      decoration.routing ||
      {};
    const rawSource = dd.source || decoration.selectedSource || "elite";
    const sourceOption = String(rawSource).startsWith("supplier|")
      ? sourceOptions.find((option) =>
          String(option?.value || "").toLowerCase() === String(rawSource).toLowerCase()
        )
      : sourceOptions.find((option) => option.value === rawSource);
    const source = sourceOption?.value || rawSource;
    // The source selector describes the routing category; it must not overwrite
    // the specific, persisted Elite decoration method during edit/copy.
    const methodName =
      (source === "elite"
        ? (dd.methodName && String(dd.methodName).trim().toLowerCase() !== "decoration network"
            ? dd.methodName
            : sourceOption?.label)
        : sourceOption?.label || dd.methodName) ||
      (source === "elite" ? "Decoration Network" : source);

    // Use any rowBindings the server gave us
    const bindings = Array.isArray(dd.rowBindings) ? dd.rowBindings : [];
    const bindByIndex = new Map(bindings.map((b) => [Number(b.rowIndex), b]));

    const savedDecoratorId = dd.decoratorId ?? null;
    const parentChargeId = dd.chargeId ?? null; // keep as-is; do NOT smear to rows

    const rows = (decoration.Charge || []).map((charge, idx) => {
      const bind = bindByIndex.get(idx) || {};

      const rowLabel = String(charge.Name ?? charge.chargeName ?? "").trim();
      const bindingLabel = String(bind.chargeName ?? "").trim();
      const sameBoundCharge = Boolean(rowLabel && bindingLabel &&
        rowLabel.toLowerCase() === bindingLabel.toLowerCase() &&
        (charge.decoratorId == null || bind.decoratorId == null ||
          String(charge.decoratorId) === String(bind.decoratorId)));
      // Charge[] describes the actual row. The binding is a fallback only if
      // it still belongs to that row (rowIndex alone is not sufficient).
      const rowChargeId = charge.chargeId ??
        (sameBoundCharge ? bind.chargeId : null) ?? null;
      const rowDecoratorId = charge.decoratorId ??
        (sameBoundCharge ? bind.decoratorId : null) ??
        savedDecoratorId ?? null;

      // ⬇ normalize productionDepartmentId to number | null
      const rowProductionDepartmentIdRaw = charge.productionDepartmentId ??
        (sameBoundCharge ? bind.productionDepartmentId : null) ?? null;
      const rowProductionDepartmentId =
        rowProductionDepartmentIdRaw == null
          ? null
          : Number(rowProductionDepartmentIdRaw);

      return {
        id:          charge.id ?? "",
        chargeName:  charge.Name ?? charge.chargeName ?? (sameBoundCharge ? bind.chargeName : ""),
        quantity:    charge.Quantity ?? 0,
        netCost:     charge.NetCost ?? 0,
        margin:      charge.Margin ?? 0,
        retail:      charge.Retail ?? 0,
        clientPrice: charge.ClientPrice ?? 0,
        total:       charge.Total ?? 0,
        type:        charge.Type || "Cost",
        subType:     charge.SubType ?? charge.subType ?? "",
        priceType:   String(charge.SubType ?? charge.subType ?? "").toLowerCase() === "run charge"
          ? "Run Charge"
          : String(charge.SubType ?? charge.subType ?? "").toLowerCase() === "fixed charge"
            ? "Fixed Charge"
            : "",
        pricingOption: normalizeDisplayStatus(charge.DisplayStatus),

        supplierId:  charge.supplierId ?? (sameBoundCharge ? bind.supplierId : null) ?? null,
        addressId:   charge.addressId ?? (sameBoundCharge ? bind.addressId : null) ?? null,
        productionInputLabel:
          charge.productionInputLabel ?? (sameBoundCharge ? bind.productionInputLabel : null) ?? null,
        productionInputValue:
          charge.productionInputValue ?? (sameBoundCharge ? bind.productionInputValue : null) ?? null,

        decoratorId:            rowDecoratorId,
        chargeId:               rowChargeId,
        productionDepartmentId: rowProductionDepartmentId,
      };
    });

    // Only keep a parent chargeId if it’s safe (prevents smearing on later saves)
    const distinct = Array.from(
      new Set(rows.map((r) => r.chargeId).filter((v) => v != null))
    );
    const allRowsShareCharge = rows.length > 0 &&
      distinct.length === 1 &&
      rows.every((row) => row.chargeId != null && String(row.chargeId) === String(distinct[0]));
    const safeParentChargeId = allRowsShareCharge
      ? distinct[0]
      : (rows.length <= 1 && distinct.length === 0 ? parentChargeId : null);

    const ddOut = {
      ...dd,
      source,
      methodName,
      decoratorId: savedDecoratorId,
      // keep inbound rowBindings for reference (not authoritative)
      rowBindings: bindings,
    };
    if (safeParentChargeId != null) ddOut.chargeId = safeParentChargeId;
    else delete ddOut.chargeId;

    return {
      id: decoration.id,
      Location: decoration.Location || defaultLocation,
      DecoratorDetails: ddOut,
      rows,
      artwork: (() => {
        const art =
          decoration.ArtWork ||
          decoration.artwork ||
          decoration.documentDecoration?.artwork ||
          {};
        return {
          id:                  art.id ?? art.ID ?? null,
          charge:              art.Charge ?? art.charge ?? 0,
          imprintType:         resolveArtworkImprintTypeForEdit(art, ddOut),
          customerFileId:      art.CustomerFileId ?? art.customerFileId ?? null,
          proofRequired:       art.ProofRequired ?? art.proofRequired ?? "",
          proofRequiredAction: art.ProofRequiredAction ?? art.proofRequiredAction ?? null,
          logoName:            art.LogoName ?? art.logoName ?? "",
          logoSize:            art.LogoSize ?? art.logoSize ?? "",
          logoColor:           art.LogoColor ?? art.logoColor ?? "",
          repeatLogo:          art.RepeatLogo ?? art.repeatLogo ?? "",
          supplierNotes:       art.SupplierNotes ?? art.supplierNotes ?? "",
          selectedImage:       art.Image ?? art.selectedImage ?? art.filePath ?? art.url ?? null,
          previewUrl:
            art.previewUrl ??
            art.PreviewUrl ??
            art.previewImageUrl ??
            art.PreviewImageUrl ??
            null,
        };
      })(),
    };
  });
};
